<template>
  <div class="s-dashboard-layout" v-if="fields">
    <div class="s-dashboard-layout__header">
      <div class="s-dashboard-layout__header-logo" @click="$redirectTo(dashboardLink)">
        <Icon class="s-dashboard-layout__header-logo-icon" size="large" :field="fields.logoIcon" />
        <JssRichText class="s-dashboard-layout__header-logo-text" :field="fields.logoText" />
      </div>
      <div class="s-dashboard-layout__header-btns">
        <div class="s-dashboard-layout__header-btns-orders" @click="$redirectTo(orderListLink)">
          <div class="s-dashboard-layout__header-btns-orders-text">
            <JssRichText :field="fields.orderText" />
          </div>
          <div class="s-dashboard-layout__header-btns-orders-number">{{ orderCount }}</div>
        </div>
        <div class="s-dashboard-layout__header-btns-menu" @click.prevent="toggleMenu" ref="menuEl">
          <div class="s-dashboard-layout__header-btns-menu-text" v-if="largeThanTablet()">
            {{ $t('Account').toUpperCase() }}
          </div>
          <Icon
            class="s-dashboard-layout__header-btns-menu-icon"
            :class="{ collapsed }"
            name="down"
          />
        </div>
      </div>
      <transition name="options-ani">
        <div class="s-dashboard-layout__menu" v-if="collapsed">
          <template v-if="fields?.menus?.length">
            <SiteLink
              class="s-dashboard-layout__menu-item"
              :class="{ active: activeMenu === menu.fields.code?.value }"
              :link="menu.fields?.link"
              v-for="menu in fields?.menus"
              :key="menu.id"
            >
              <div class="s-dashboard-layout__menu-item-icon">
                <Icon :field="menu.fields?.icon" />
              </div>
              <div class="s-dashboard-layout__menu-item-link">
                {{ menu.fields?.link?.value?.text }}
              </div>
            </SiteLink>
          </template>
          <div
            class="s-dashboard-layout__menu-item s-dashboard-layout__sign-out"
            @click="onSignOut"
          >
            <JssText :field="fields.signOutText" />
          </div>
        </div>
      </transition>
    </div>
    <div class="s-dashboard-layout__main" v-show="loaded">
      <slot />
    </div>
  </div>
</template>
<script>
import {
  getCurrentInstance,
  reactive,
  toRefs,
  onMounted,
  onBeforeUnmount,
  inject,
  computed,
} from 'vue';
import useAppStore from '@/store/appStore';
import api from '@/api';
import services from '@/services';
import { eq, formatString } from '@/utils/string-utils';
import { isNoE, deepCopy } from '@/utils/obj-utils';
import { appendQuery, getBetterUrl } from '@/utils/uri-utils';
import { getGlobalConfigs, getPageAlpha2Code } from '@/utils/site-utils';
import { arraySort } from '@/utils/array-utils';
import { largeThanTablet } from '@/utils/dom-utils';
export default {
  name: 'DashboardLayout',
  props: {
    fields: {
      type: Object,
    },
    activeMenu: {
      type: String,
      default: 'dashboard',
    },
  },
  setup(props) {
    if (!props.fields) return;
    const toast = inject('toast');
    const appStore = useAppStore();
    const appMethods = inject('appMethods');
    const { proxy } = getCurrentInstance();
    const { $jss } = proxy;
    const page = $jss.routeData();
    const alpha2Code = getPageAlpha2Code(page);
    const state = reactive({
      menuEl: null,
      orders: [],
      collapsed: false,
      dashboardLink: null,
      orderListLink: null,
      orderDetailsLink: null,
      mhpLink: null,
      mallOrders: [],
      mallOrderDetailsLink: null,
      loaded: false,
    });
    const computes = {
      orderCount: computed(() => state.orders.length + state.mallOrders.length),
    };
    const methods = {
      largeThanTablet,
      getOrders: async () => {
        let vinList = [];
        const [vinRes, vinEx] = await api.cidp.getControlCars();
        if (vinEx) {
          await toast.showEx(vinEx);
          if (vinEx.code === 60000000) {
            await appMethods.logoutToLogin();
          }
          return;
        }
        if (vinRes?.length) {
          vinList = vinRes.map((v) => v.vin);
        }
        const [res, ex] = await api.order.intactList(null, {
          customerId: appStore?.loginInfo?.levcId,
          pageNum: 1,
          pageSize: 10000,
          vinList,
        });
        if (ex) {
          await toast.showEx(ex);
          if (ex.code === 60000000) {
            await appMethods.logoutToLogin();
          }
          return;
        }
        if (!res) return;
        let orders = [];
        let ruleItems = [];
        const orderStatuses = await services.order.getOrderStatuses();
        orders = deepCopy(
          res?.filter(
            (x) =>
              x.orderStatus && x.orderType && !eq(x.orderStatus, 'New') && !eq(x.orderStatus, '100')
          )
        );
        orders = orders.filter(
          (x) =>
            !eq(x.countryCode, 'GB') ||
            !eq(x.orderType, '100') ||
            !eq(x.stockFlag, '2') ||
            !x.stockOrder
        );
        for (let order of orders) {
          const carSeries = [];
          order.carSeries = carSeries;
          const carModel = [];
          order.carModel = carModel;
          order.status = orderStatuses.find((x) => x.code == order.orderStatus);
          order.soldOut = !!carSeries?.soldOut;
          if (order.orderType === '200') {
            order.modelImage = order.carImages?.length > 0 ? order.carImages[0].imageUrl : null;
          } else {
            order.modelImage = carSeries?.image?.src;
          }
          if (eq(order.orderType, '100') && eq(order.orderStatus, '200')) {
            ruleItems.push({
              orderType: '200',
              carSeriesCode: carSeries?.code ?? '',
              country: order.countryCode,
            });
          }
          order.userUpdateTime = null;
          if (order.intactOrderDateDto) {
            let { finalPayCompleteTime, paymentTime, signTime } = order.intactOrderDateDto;
            finalPayCompleteTime = finalPayCompleteTime
              ? new Date(finalPayCompleteTime).getTime()
              : 0;
            paymentTime = paymentTime ? new Date(paymentTime).getTime() : 0;
            signTime = signTime ? new Date(signTime).getTime() : 0;
            order.userUpdateTime =
              finalPayCompleteTime > paymentTime
                ? finalPayCompleteTime > signTime
                  ? finalPayCompleteTime
                  : signTime
                : paymentTime;
          }
        }
        const [resConfig] = await api.order.rule.query(null, { items: ruleItems });
        orders.forEach((o) => {
          o.jumpLink = null;
          o.configurable = resConfig.some((r) => eq(r.carCode, o.series)) ? true : false;
          if (state.orderDetailsLink && eq(o.orderType, '200')) {
            const href = getBetterUrl(state.orderDetailsLink, page.itemLanguage, false);
            o.jumpLink = appendQuery(href, { orderId: o.id });
            o.salesOrder = true;
          } else if (
            state.mhpLink &&
            eq(o.orderType, '100') &&
            eq(o.orderStatus, '200') &&
            (isNoE(o.series) || isNoE(o.model) || o.configurable)
          ) {
            const [, targetLang] = services.order.checkOrderLang(page, o.countryCode);
            if (o.soldOut) {
              o.jumpLink = null;
            } else if (o.carSeries?.noConfigure || (o.stockOrder && o.stockFlag == 2)) {
              const detailsHref = getBetterUrl(state.orderDetailsLink, page.itemLanguage, false);
              o.jumpLink = appendQuery(detailsHref, { orderId: o.id });
            } else {
              const href = formatString(state.mhpLink, {
                lang: targetLang,
                series: o.series?.toLowerCase(),
                model: '',
              });
              o.jumpLink = appendQuery(href, {
                access_token: appStore?.loginInfo?.token,
                blindOrderId: o.id,
              });
            }
            o.salesOrder = false;
          } else if (eq(o.orderType, '800')) {
            const href = getBetterUrl(state.orderDetailsLink, page.itemLanguage, false);
            o.jumpLink = appendQuery(href, { orderId: o.id });
            o.salesOrder = true;
          }
        });
        orders = arraySort(orders, 'userUpdateTime');
        state.orders = orders;
        appStore.setOrderInfo(orders);
        state.loaded = true;
        // loading.hide();
      },
      getMallOrders: async () => {
        const [res, ex] = await api.shop.getOrderList(null, {
          pageNum: 0,
          pageSize: 10000,
        });
        if (ex) {
          await toast.showEx(ex);
          if (ex.code === 60000000) {
            await appMethods.logoutToLogin();
          }
          return;
        }
        if (!res || !res.records || res.records.length < 1) return;
        const orderStatuses = await services.order.getOrderStatuses();
        const { $jss } = proxy;
        const page = $jss.routeData();
        const mallOrderdetailsHref = getBetterUrl(
          state.mallOrderDetailsLink,
          page.itemLanguage,
          false
        );
        for (let order of res.records) {
          order.status = orderStatuses.find((x) => x.code == order.status);
          order.series = order.items[0].name;
          order.paymentTime = order.payTime;
          order.userUpdateTime = new Date(order.payTime).getTime();
          order.createTime = order.dateCreate;
          order.orderId = order.orderCode;
          order.jumpLink = appendQuery(mallOrderdetailsHref, { orderCode: order.orderCode });
        }
        let orders = arraySort(res.records, 'userUpdateTime');
        state.mallOrders = res.records;
        appStore.setMallOrderInfo(orders);
      },
      toggleMenu: () => {
        state.collapsed = !state.collapsed;
      },
      closeMenu: (e) => {
        if (!state.menuEl?.contains(e.target)) {
          state.collapsed = false;
        }
      },
      onSignOut: async () => {
        await appMethods.logout();
      },
    };
    onMounted(async () => {
      // loading.show();
      const { $jss } = proxy;
      const page = $jss.routeData();
      const [dashboardLink, orderListLink, orderDetailsLink, mhpLink, mallOrderDetailsLink] =
        getGlobalConfigs(
          page,
          'dashboardLink',
          'orderListLink',
          'orderDetailsLink',
          'mhpLink',
          'mallOrderDetailsLink'
        );
      state.dashboardLink = dashboardLink?.value?.href;
      state.orderListLink = orderListLink?.value?.href;
      state.orderDetailsLink = orderDetailsLink?.value?.href;
      state.mallOrderDetailsLink = mallOrderDetailsLink?.value?.href;
      state.mhpLink = mhpLink?.value;
      await methods.getOrders();
      if (!eq(alpha2Code, 'GB')) {
        await methods.getMallOrders();
      }
      document.addEventListener('click', (e) => methods.closeMenu(e), true);
    });
    onBeforeUnmount(() => {
      document.removeEventListener('click', (e) => methods.closeMenu(e), true);
    });
    return {
      ...toRefs(state),
      ...computes,
      ...methods,
      appStore,
    };
  },
};
</script>
<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';
.s-dashboard-layout {
  position: relative;
  &__header {
    position: sticky;
    top: 0;
    left: 0;
    height: 56px;
    padding: 0 24px;
    display: flex;
    flex: row;
    align-items: center;
    justify-content: space-between;
    z-index: 2;
    background-color: $white;
    transition: all 600ms ease;
    &-logo,
    &-btns,
    &-btns-orders,
    &-btns-menu {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    &-logo {
      gap: 16px;
      cursor: pointer;
      &-text {
        color: #262626;
      }
    }
    &-btns {
      height: 100%;
      gap: 0;
      &-orders {
        height: 100%;
        gap: 8px;
        cursor: pointer;
        &-text {
          font-size: 12px;
          line-height: 16px;
          color: #2f2f2f;
        }
        &-number {
          width: 24px;
          height: 24px;
          font-size: 12px;
          line-height: 20px;
          color: $black;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: $yellow;
        }
      }
      &-menu {
        height: 100%;
        padding: 0 14px;
        gap: 16px;
        cursor: pointer;
        &-text {
          font-size: 12px;
          line-height: 16px;
          color: #2f2f2f;
        }
        &-icon {
          width: 12px;
          height: 12px;
          transition: transform 0.5s ease;
          > svg {
            width: 100%;
            height: 100%;
          }
          &.collapsed {
            transform: rotate(180deg);
          }
        }
      }
    }
  }
  &__menu {
    position: absolute;
    top: 60px;
    right: 48px;
    background-color: $white;
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    box-shadow: 0 1px 2px rgba($grey-fade, 0.5);
    z-index: 2;
    &-item {
      display: inline-block;
      width: auto;
      padding: 8px 24px;
      line-height: 24px;
      color: $black !important;
      cursor: pointer;
      &.active {
        font-weight: bold;
      }
      &:hover {
        background-color: rgba(#ccc, 0.5);
      }
    }
  }
  @include tablet-landscape {
    &__header {
      padding: 0 48px;
      &-btns {
        gap: 64px;
        &-orders {
          gap: 16px;
        }
      }
    }
  }
}
</style>
